import { Link } from "react-router-dom";
import { ArrowRight, Crosshair, Telescope } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import ProcessFlow from "@/components/ProcessFlow";
import { Container, Eyebrow, Reveal, SectionHead } from "@/components/Motion";
import { APPROACH_STEPS, IMAGES, MISSION, VALUES, VISION } from "@/data/content";

export default function About() {
  return (
    <>
      <SEO
        title="About EVOVLEX IT | Secure, Innovative & Scalable IT Solutions"
        description="EVOVLEX IT is a technology solutions company delivering secure, innovative and scalable IT solutions at the intersection of technology, cybersecurity, cloud, software engineering and digital transformation."
      />
      <PageHero
        eyebrow="About Us"
        titleLines={["About", { node: <span className="text-gradient-brand">EVOVLEX IT</span> }]}
        sub="A technology solutions company focused on delivering secure, innovative and scalable IT solutions for modern businesses."
        image={IMAGES.about}
      />

      <section data-testid="about-intro-section" className="bg-white py-20 lg:py-28">
        <Container>
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
            <div>
              <SectionHead eyebrow="Who We Are" title="Building Stronger, Smarter Digital Ecosystems" />
              <Reveal delay={0.2}>
                <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
                  We work at the intersection of technology, cybersecurity, cloud computing, software engineering and
                  digital transformation to help organizations build stronger and smarter digital ecosystems.
                </p>
              </Reveal>
              <Reveal delay={0.28}>
                <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
                  Our approach combines technical expertise with a clear understanding of business requirements.
                  Whether you are building a new digital product, modernizing your infrastructure or strengthening
                  your cybersecurity posture, EVOVLEX IT provides solutions designed for long-term value.
                </p>
              </Reveal>
              <Reveal delay={0.36}>
                <div className="mt-8 flex flex-wrap gap-2.5">
                  {["Technology", "Cybersecurity", "Cloud Computing", "Software Engineering", "Digital Transformation"].map(
                    (t) => (
                      <span
                        key={t}
                        className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-700"
                      >
                        {t}
                      </span>
                    ),
                  )}
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <div className="relative overflow-hidden rounded-2xl shadow-2xl shadow-navy-950/20">
                <img
                  src={IMAGES.about}
                  alt="Modern enterprise architecture"
                  className="aspect-[4/3] w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-navy-950/40 via-transparent to-transparent" />
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      <section data-testid="about-approach-section" className="relative overflow-hidden bg-navy-950 py-20 lg:py-28 grain">
        <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
        <Container className="relative">
          <SectionHead
            dark
            center
            eyebrow="How We Work"
            title="Understand. Strategize. Build. Secure. Scale."
          />
          <div className="mt-14">
            <ProcessFlow steps={APPROACH_STEPS} dark testid="about-approach-process" />
          </div>
        </Container>
      </section>

      <section data-testid="about-mission-vision-section" className="bg-slate-50 py-20 lg:py-28">
        <Container>
          <div className="grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div
                data-testid="mission-card"
                className="relative h-full overflow-hidden rounded-2xl border border-slate-800 bg-navy-950 p-10 text-white lg:p-12"
              >
                <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
                <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-blue-600/20 blur-[80px]" aria-hidden="true" />
                <div className="relative">
                  <Crosshair className="h-8 w-8 text-cyan-400" strokeWidth={1.6} />
                  <h2 className="mt-6 font-display text-2xl font-bold sm:text-3xl">Our Mission</h2>
                  <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">{MISSION}</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div
                data-testid="vision-card"
                className="relative h-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-10 lg:p-12"
              >
                <div className="absolute -left-16 -bottom-16 h-48 w-48 rounded-full bg-cyan-500/10 blur-[80px]" aria-hidden="true" />
                <div className="relative">
                  <Telescope className="h-8 w-8 text-evo-blue" strokeWidth={1.6} />
                  <h2 className="mt-6 font-display text-2xl font-bold text-slate-900 sm:text-3xl">Our Vision</h2>
                  <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{VISION}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      <section data-testid="about-values-section" className="bg-white py-20 lg:py-28">
        <Container>
          <SectionHead eyebrow="Our Values" title="What We Stand For" />
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={0.06 * i} className="h-full">
                <div
                  data-testid={`value-card-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group h-full rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-500/40 hover:shadow-xl hover:shadow-blue-600/5"
                >
                  <span className="font-mono text-xs font-semibold tracking-widest text-slate-300 transition-colors group-hover:text-orange-500">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <v.icon className="mt-4 h-7 w-7 text-evo-blue" strokeWidth={1.7} />
                  <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-col items-start justify-between gap-6 rounded-2xl border border-slate-200 bg-slate-50 p-8 sm:flex-row sm:items-center">
              <p className="max-w-xl font-display text-xl font-semibold text-slate-900">
                Ready to build something that lasts?
              </p>
              <Link
                to="/contact"
                data-testid="about-cta-talk-to-expert"
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-300 hover:-translate-y-0.5"
              >
                Talk to an Expert
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
